import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetTransportImpactTotals extends Result {
  result: {
    transport_impact: IImpact;
    company_impact: IImpact;
  };
}

export function useTransportImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "transport", "transportImpactTotals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetTransportImpactTotals>("/dashboard/transport/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

interface GetTransportImpactTopContributors {
  transport_impact: ({
    travel_mode: string;
    distance: number;
  } & IImpact)[];
  company_transport_impact: IImpact;
}

export function useTransportImpactTopContributors({ metric }: { metric: ImpactMetricKey }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "transport",
      "transportImpactTopContributors",
      startDate,
      endDate,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetTransportImpactTopContributors>("/dashboard/transport/top_contributors", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            sort_order: "descend",
            sort_by: metric,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

interface GetTransportImpactDetail extends Result {
  result: {
    total_rows: number;
    transport_impact: ({
      arrival_name: string;
      departure_name: string;
      total_product_mass: number;
      distance: number;
      transport_modes: string[];
    } & IImpact)[];
    company_transport_impact: IImpact;
  };
}

export function useTransportImpactDetail({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "transport",
      "transportImpactDetail",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetTransportImpactDetail>("/dashboard/transport/transport_impact_detail", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export function useTransportImpactDetailExport({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/transport/transport_impact_detail", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}

interface GetTransportImpactTotalsOverTime extends Result {
  result: {
    transport_impact: { label: string; data: ({ year: number; month: number } & IImpact)[] }[];
  };
}

export function useTransportImpactTotalsOverTime() {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "transport", "transportImpactTotalsOverTime", startDate, endDate],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetTransportImpactTotalsOverTime>("/dashboard/transport/totals_over_time", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

interface GetTransportImpactPerType extends Result {
  result: { label: string; transport_impact: IImpact | null }[];
}

export function useTransportImpactPerType({ metric }: { metric: ImpactMetricKey }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "transport", "transportImpactPerType", startDate, endDate, metric],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetTransportImpactPerType>("/dashboard/transport/inbound_vs_outbound", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}
